import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from 'common';
import MaterialTable from 'material-table';
import { Box, Grid, Typography, TextField, Autocomplete, Alert } from '@mui/material';
import moment from 'moment/min/moment-with-locales';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ManageHistoryOutlinedIcon from '@mui/icons-material/ManageHistoryOutlined';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton } from './C2Dialog';
import {
  C2,
  C2_FONT,
  C2TableCard,
  C2SectionHeading,
  C2StatusChip,
} from './Table/C2UserTable';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
  '& .MuiFormHelperText-root': { fontFamily: C2_FONT },
};

const fullName = (u) => `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—';

function DetailRow({ label, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        py: 0.8,
        borderTop: `1px solid ${C2.cellBorder}`,
      }}
    >
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>{label}</Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', fontWeight: 700, color: C2.dark, textAlign: 'right' }}>
        {children}
      </Typography>
    </Box>
  );
}

function CheckRow({ ok, label }) {
  const Icon = ok ? CheckCircleRoundedIcon : CancelRoundedIcon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
      <Icon sx={{ fontSize: 18, color: ok ? '#16A34A' : '#DC2626' }} />
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.83rem', color: ok ? C2.dark : '#B91C1C' }}>{label}</Typography>
    </Box>
  );
}

function PersonCard({ title, children }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '14px',
        border: `1px solid ${C2.border}`,
        backgroundColor: C2.bg,
        height: '100%',
      }}
    >
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: C2.violet, mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * Pestaña "Cambio de líder" (Red de Líderes).
 * El admin reemplaza un líder por un conductor de la misma ciudad fuera de toda red.
 */
export default function LeaderChangePanel({
  leaders,
  staticusers,
  cityFilter,
  settings,
  tableStyle,
  tableOptions,
  emptyLocalization,
  onChanged,
}) {
  const canEdit = !!settings?.AllowCriticalEditsAdmin;
  const [oldLeader, setOldLeader] = useState(null);
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [changes, setChanges] = useState([]);
  const [loadingChanges, setLoadingChanges] = useState(false);

  const loadChanges = useCallback(async () => {
    if (!api.fetchLeaderChanges) return;
    setLoadingChanges(true);
    try {
      setChanges(await api.fetchLeaderChanges());
    } catch (e) {
      setChanges([]);
    } finally {
      setLoadingChanges(false);
    }
  }, []);

  useEffect(() => {
    loadChanges();
  }, [loadChanges]);

  const leaderOptions = useMemo(() => {
    const list = cityFilter ? leaders.filter((l) => l.cityId === cityFilter) : leaders;
    return [...list].sort((a, b) => fullName(a).localeCompare(fullName(b), 'es'));
  }, [leaders, cityFilter]);

  useEffect(() => {
    if (oldLeader && !leaderOptions.some((l) => l.id === oldLeader.id)) setOldLeader(null);
  }, [leaderOptions, oldLeader]);

  const networkOf = useCallback(
    (uid) => {
      const members = new Set();
      const directs = new Set();
      (staticusers || []).forEach((u) => {
        if (u.id === uid) return;
        if (u.principalUid === uid) members.add(u.id);
        if (u.sponsorUid === uid || u.signupViaReferral === uid) {
          members.add(u.id);
          directs.add(u.id);
        }
      });
      return { members: members.size, directs: directs.size };
    },
    [staticusers]
  );

  const oldNetwork = useMemo(() => (oldLeader ? networkOf(oldLeader.id) : null), [oldLeader, networkOf]);

  const normalizedEmail = email.trim().toLowerCase();
  const candidate = useMemo(() => {
    if (!normalizedEmail) return null;
    return (staticusers || []).find((u) => String(u.email || '').trim().toLowerCase() === normalizedEmail) || null;
  }, [staticusers, normalizedEmail]);

  const checks = useMemo(() => {
    if (!oldLeader || !normalizedEmail) return null;
    const net = candidate && api.normalizeNetworkState ? api.normalizeNetworkState(candidate, candidate.id) : { inNetwork: false };
    const list = [
      { ok: !!candidate, label: candidate ? 'Usuario encontrado' : 'No hay un usuario con ese correo' },
    ];
    if (candidate) {
      list.push(
        { ok: candidate.id !== oldLeader.id, label: candidate.id !== oldLeader.id ? 'Es distinto al líder actual' : 'Es el mismo líder actual' },
        { ok: candidate.usertype === 'driver', label: candidate.usertype === 'driver' ? 'Es conductor' : 'No es conductor' },
        {
          ok: !!oldLeader.cityId && candidate.cityId === oldLeader.cityId,
          label:
            candidate.cityId === oldLeader.cityId
              ? `Misma ciudad (${oldLeader.cityName || '—'})`
              : `Otra ciudad (${candidate.cityName || 'sin ciudad'})`,
        },
        {
          ok: !net.inNetwork && candidate.signupMode !== 'city_special',
          label: !net.inNetwork && candidate.signupMode !== 'city_special' ? 'No pertenece a ninguna red' : 'Ya pertenece a una red',
        }
      );
    }
    return list;
  }, [oldLeader, candidate, normalizedEmail]);

  const allChecksOk = !!checks && checks.every((c) => c.ok);

  const formatCop = (v) => (api.formatCop ? api.formatCop(Number(v) || 0) : `$${Number(v) || 0}`);

  const weeklyLabel = (u) => {
    const w = api.formatWeeklyProgress ? api.formatWeeklyProgress(u.leaderWeekly) : null;
    return w ? `Cond. ${w.driversLabel} · Usu. ${w.customersLabel}` : '—';
  };

  const handleSubmit = async () => {
    if (!oldLeader || !allChecksOk || !canEdit) return;
    setSubmitting(true);
    const res = await api.replaceNetworkLeader({
      oldLeaderUid: oldLeader.id,
      newLeaderEmail: normalizedEmail,
      reason: reason.trim(),
    });
    setSubmitting(false);
    setConfirmOpen(false);
    if (res?.success) {
      setResult({
        type: 'success',
        text: `${fullName(candidate)} ahora es líder en ${oldLeader.cityName || 'la ciudad'}. Se transfirieron ${res.movedMembers} integrantes (${res.movedDirects} directos).`,
      });
      setOldLeader(null);
      setEmail('');
      setReason('');
      loadChanges();
      if (onChanged) onChanged();
    } else {
      setResult({ type: 'error', text: res?.error || 'No se pudo completar el cambio de líder.' });
    }
  };

  const hideEmail = (value) => (canEdit ? value || '—' : '••••••');

  const historyColumns = [
    {
      title: 'Fecha',
      field: 'createdAt',
      render: (r) => (r.createdAt ? moment(r.createdAt).format('lll') : '—'),
    },
    { title: 'Ciudad', field: 'cityName', render: (r) => r.cityName || '—' },
    {
      title: 'Líder saliente',
      field: 'oldLeaderName',
      render: (r) => (
        <Box>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.oldLeaderName || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>{hideEmail(r.oldLeaderEmail)}</Typography>
        </Box>
      ),
    },
    {
      title: 'Nuevo líder',
      field: 'newLeaderName',
      render: (r) => (
        <Box>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.newLeaderName || '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>{hideEmail(r.newLeaderEmail)}</Typography>
        </Box>
      ),
    },
    { title: 'Correo saliente', field: 'oldLeaderEmail', hidden: true },
    { title: 'Correo nuevo', field: 'newLeaderEmail', hidden: true },
    {
      title: 'Red transferida',
      field: 'movedMembers',
      render: (r) => (
        <C2StatusChip tone="violet" dot={false} label={`${r.movedMembers || 0} integrantes · ${r.movedDirects || 0} directos`} />
      ),
    },
    { title: 'Motivo', field: 'reason', render: (r) => r.reason || '—' },
    { title: 'Admin', field: 'adminEmail', render: (r) => hideEmail(r.adminEmail) },
  ];

  const filteredChanges = cityFilter ? changes.filter((c) => c.cityId === cityFilter) : changes;

  return (
    <>
      <C2TableCard>
        <C2SectionHeading
          title="Cambio de líder"
          subtitle="Reemplaza un líder que no cumple objetivos por un conductor de la misma ciudad que no pertenezca a ninguna red."
        />

        {result ? (
          <Alert
            severity={result.type}
            onClose={() => setResult(null)}
            sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}
          >
            {result.text}
          </Alert>
        ) : null}

        {!canEdit ? (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            Los cambios de líder están deshabilitados en modo demo.
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PersonCard title="1 · Líder actual">
              <Autocomplete
                options={leaderOptions}
                value={oldLeader}
                onChange={(_, v) => {
                  setOldLeader(v);
                  setResult(null);
                }}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                getOptionLabel={(o) => `${fullName(o)}${o.cityName ? ` · ${o.cityName}` : ''}`}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{fullName(o)}</Typography>
                      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>
                        {[o.cityName, canEdit ? o.email : null].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="Sin líderes"
                renderInput={(params) => <TextField {...params} label="Selecciona el líder a reemplazar" sx={fieldSx} />}
              />
              {oldLeader ? (
                <Box sx={{ mt: 1.5 }}>
                  <DetailRow label="Correo">{hideEmail(oldLeader.email)}</DetailRow>
                  <DetailRow label="Ciudad">{oldLeader.cityName || '—'}</DetailRow>
                  <DetailRow label="Cupos Nivel 1">
                    {`${Number(oldLeader.directSlotsUsed) || 0}/${oldLeader.directSlotsTotal != null ? oldLeader.directSlotsTotal : 50}`}
                  </DetailRow>
                  <DetailRow label="Semana actual">{weeklyLabel(oldLeader)}</DetailRow>
                  <DetailRow label="Red a transferir">
                    {`${oldNetwork?.members || 0} integrantes · ${oldNetwork?.directs || 0} directos`}
                  </DetailRow>
                  <DetailRow label="Saldo de red (lo conserva)">{formatCop(oldLeader.walletReferral)}</DetailRow>
                </Box>
              ) : null}
            </PersonCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PersonCard title="2 · Nuevo líder">
              <TextField
                fullWidth
                type="email"
                label="Correo del nuevo líder"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setResult(null);
                }}
                disabled={!oldLeader}
                helperText={oldLeader ? 'Conductor de la misma ciudad, fuera de toda red.' : 'Primero selecciona el líder actual.'}
                sx={fieldSx}
              />
              {candidate && oldLeader ? (
                <Box sx={{ mt: 1.5 }}>
                  <DetailRow label="Nombre">{fullName(candidate)}</DetailRow>
                  <DetailRow label="Ciudad">{candidate.cityName || '—'}</DetailRow>
                </Box>
              ) : null}
              {checks ? <Box sx={{ mt: 1.5 }}>{checks.map((c) => <CheckRow key={c.label} ok={c.ok} label={c.label} />)}</Box> : null}
            </PersonCard>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Motivo del cambio (opcional)"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              sx={fieldSx}
            />
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <C2PrimaryButton
              startIcon={<SwapHorizRoundedIcon />}
              disabled={!canEdit || !oldLeader || !allChecksOk || submitting}
              onClick={() => setConfirmOpen(true)}
            >
              Cambiar líder
            </C2PrimaryButton>
          </Grid>
        </Grid>
      </C2TableCard>

      <Box sx={{ mt: 2 }}>
        <C2TableCard>
          <C2SectionHeading title="Historial de cambios de líder" subtitle="Registro de reemplazos realizados por el equipo administrativo." />
          <MaterialTable
            title="Historial de cambios de líder"
            columns={historyColumns}
            data={filteredChanges}
            isLoading={loadingChanges}
            style={tableStyle}
            options={tableOptions}
            localization={emptyLocalization(ManageHistoryOutlinedIcon, 'Sin cambios de líder', 'Cuando reemplaces un líder, el cambio quedará registrado aquí.')}
          />
        </C2TableCard>
      </Box>

      <C2Dialog
        open={confirmOpen}
        onClose={() => (submitting ? null : setConfirmOpen(false))}
        title="Confirmar cambio de líder"
        icon={SwapHorizRoundedIcon}
        maxWidth={480}
        actions={
          <>
            <C2SecondaryButton onClick={() => setConfirmOpen(false)} disabled={submitting}>
              Cancelar
            </C2SecondaryButton>
            <C2PrimaryButton onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Cambiando…' : 'Confirmar cambio'}
            </C2PrimaryButton>
          </>
        }
      >
        {oldLeader && candidate ? (
          <Box sx={{ fontFamily: C2_FONT, color: C2.dark, fontSize: '0.9rem', lineHeight: 1.55 }}>
            <Typography sx={{ fontFamily: C2_FONT, mb: 1 }}>
              <b>{fullName(candidate)}</b> reemplazará a <b>{fullName(oldLeader)}</b> como líder en {oldLeader.cityName || 'la ciudad'}.
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
              <li>Toda la red ({oldNetwork?.members || 0} integrantes, {oldNetwork?.directs || 0} directos) pasa al nuevo líder, junto con sus cupos usados.</li>
              <li>Los bonos futuros de la red serán para el nuevo líder (techo {formatCop(api.EARNINGS_CAP_LEADER || 50000000)}).</li>
              <li>{fullName(oldLeader)} queda como conductor independiente: techo {formatCop(api.EARNINGS_CAP_STANDARD || 30000000)}, sin cupos ni meta semanal.</li>
              <li>Conserva su saldo de red de {formatCop(oldLeader.walletReferral)} y puede retirarlo normalmente.</li>
            </Box>
            <Typography sx={{ fontFamily: C2_FONT, mt: 1, fontWeight: 700, color: '#B91C1C' }}>Esta acción no se puede deshacer automáticamente.</Typography>
          </Box>
        ) : null}
      </C2Dialog>
    </>
  );
}
